import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  useColorMode,
  useColorModeValue,
  Link,
} from "@chakra-ui/react";
import TopNav from "./components/TopNav";
import MobileNav from "./components/MobileNav";
import { apiFetch } from "./utils/api";
import { logFrontendError } from "./utils/logger";
import { Helmet } from "react-helmet";

export default function Terms() {
  const [isMobile, setIsMobile] = useState(false);
  const [prizePool, setPrizePool] = useState(0.0);
  const [countdownToNoon, setCountdownToNoon] = useState("");
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { colorMode, toggleColorMode } = useColorMode();

  const toggleSearchBar = () => setShowSearchBar((prev) => !prev);

  const handleSearch = (e) => {
    const value = (e?.target?.value || "").replace(/\D/g, "").slice(0, 6);
    setSearchTerm(value);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchPrizePool() {
      try {
        const data = await apiFetch("/app/state");

        if (!cancelled) {
          setPrizePool(Number(data.prizePool || 0));
        }
      } catch (error) {
        logFrontendError(
          "Failed to fetch prize pool (Terms)",
          error?.stack || error?.message || "",
        );
      }
    }

    fetchPrizePool();

    const intervalId = setInterval(fetchPrizePool, 10000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();

      const target = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          21,
          0,
          0,
          0,
        ),
      );

      if (now >= target) {
        target.setUTCDate(target.getUTCDate() + 1);
      }

      const diff = target - now;
      const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");

      setCountdownToNoon(`${h}:${m}:${s}`);
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  const cardBg = useColorModeValue("cardBg", "cardBg");
  const textColor = useColorModeValue("textColor", "textColor");
  const mutedText = useColorModeValue("mutedText", "mutedText");

  return (
    <Box position="relative" minH="100vh" overflow="hidden" color={textColor}>
      <Helmet>
        <title>Lotoro — Terms of Service</title>

        <meta
          name="description"
          content="Terms of Service for Lotoro, a fair, transparent crypto lottery with daily USDT payouts."
        />

        <meta property="og:title" content="Lotoro — Terms of Service" />

        <meta
          property="og:description"
          content="Read the Terms of Service for Lotoro’s blockchain-based lottery."
        />

        <meta
          property="og:image"
          content="https://lotoro.co/lotoro-og-image.png"
        />

        <meta property="og:url" content="https://lotoro.co/terms" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="twitter:title" content="Lotoro — Terms of Service" />

        <meta
          name="twitter:description"
          content="Read the Terms of Service for Lotoro’s blockchain-based lottery."
        />

        <meta
          name="twitter:image"
          content="https://lotoro.co/lotoro-og-image.png"
        />

        <link rel="canonical" href="https://lotoro.co/terms" />
      </Helmet>

      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg={
          colorMode === "light"
            ? "radial-gradient(circle at 50% 30%, #f5f5f5, #e0e0e0, #ffffff)"
            : "radial-gradient(circle at 50% 30%, #0D0D0D, #000000)"
        }
        zIndex="base"
      />

      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="rgba(255, 255, 255, 0.05)"
        backdropFilter="blur(40px)"
        zIndex="base"
      />

      <Box position="relative" zIndex="1" p={4}>
        <MobileNav
          colorMode={colorMode}
          toggleColorMode={toggleColorMode}
          toggleSearchBar={toggleSearchBar}
          showSearchBar={showSearchBar}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          countdownToNoon={countdownToNoon}
          prizePool={prizePool}
        />

        <TopNav
          colorMode={colorMode}
          toggleColorMode={toggleColorMode}
          prizePool={prizePool}
          countdownToNoon={countdownToNoon}
          showSearchBar={showSearchBar}
          toggleSearchBar={toggleSearchBar}
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          isMobile={isMobile}
        />

        <VStack
          maxW="4xl"
          mx="auto"
          mb="8px"
          spacing={6}
          align="flex-start"
          py={10}
          px={{ base: 4, md: 8 }}
          bg={cardBg}
          borderRadius="2xl"
          boxShadow="lg"
        >
          <Heading size="lg">Terms of Service</Heading>

          <Text fontSize="sm" color={mutedText}>
            Last updated: 8/28/2025
          </Text>

          <Text>
            Welcome to Lotoro, a blockchain-based daily lottery platform. By
            using our website, applications, or smart contracts, you agree to
            the following terms and conditions.
          </Text>

          <Heading size="md">1. Eligibility</Heading>

          <Text>
            You must be at least 18 years old, or the legal age of majority in
            your jurisdiction. You are responsible for ensuring that
            participation in a crypto-based lottery is legal where you reside.
            Access is prohibited where restricted or illegal.
          </Text>

          <Heading size="md">2. Wallet & Account</Heading>

          <Text>
            Lotoro does not custody funds or manage private keys. Keep your
            wallet credentials secure. Actions from your connected wallet are
            considered authorized by you.
          </Text>

          <Heading size="md">3. Tickets & Gameplay</Heading>

          <Text>
            Tickets are purchased in USDT via supported networks (Currently only
            BNB Chain). Prices and fees may change. Winners are determined
            mathematically based on randomly generated six-digit numbers,
            matched against our published tier system. Results are final and
            verifiable on-chain.
          </Text>

          <Heading size="md">4. Payouts</Heading>

          <Text>
            Payouts are sent to the wallet that purchased the winning ticket(s).
            Transactions are final once sent on-chain.
          </Text>

          <Heading size="md">5. Email & Notifications</Heading>

          <Text>
            Providing an email is mandatory but it will only be used for
            notifications. Not receiving an email does not affect prize
            eligibility.
          </Text>

          <Heading size="md">6. Prohibited Activities</Heading>

          <Text>
            No fraud or exploitation of smart contracts; no bypassing limits; no
            automated bots for unfair advantage; no illegal activity, including
            money laundering. Violations may result in suspension or ban.
          </Text>

          <Heading size="md">7. Fees</Heading>

          <Text>
            Network (gas) fees are outside our control. Any applicable service
            fees are displayed before you confirm a transaction.
          </Text>

          <Heading size="md">8. Risk Disclaimer</Heading>

          <Text>
            Participation involves risk. Crypto transactions are irreversible
            and values may fluctuate. We do not guarantee winnings or
            uninterrupted access. Network issues may cause delays.
          </Text>

          <Heading size="md">9. Limitation of Liability</Heading>

          <Text>
            Lotoro is not liable for direct, indirect, incidental, or
            consequential damages arising from use of the platform, including
            loss of funds, technical failures, or legal issues in your
            jurisdiction.
          </Text>

          <Heading size="md">10. Dispute Resolution</Heading>

          <Text>
            Disputes shall be resolved by binding arbitration under the laws of
            the jurisdiction in which Lotoro operates. You waive participation
            in class actions against Lotoro.
          </Text>

          <Heading size="md">11. Changes to Terms</Heading>

          <Text>
            We may update these Terms at any time. Continued use after changes
            constitutes acceptance. The latest version is available on this
            page.
          </Text>

          <Heading size="md">12. Contact</Heading>

          <Text>
            Questions? Contact{" "}
            <Link href="mailto:support@lotoro.co" color="teal.400">
              support@lotoro.co
            </Link>
            .
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}
